<template>
  <BaseNavigationBar title="Редактирование шаблона" />

  <BaseIsland size="lg">
    <form class="without-template-edit" @submit.prevent="handleSave">
      <TemplateMediaSlot
        :model-value="imageForUpload"
        class="without-template-edit__media-slot"
        @update:model-value="handleUpdateImage"
      />

      <BaseForm
        ref="formRef"
        v-model="formModel"
        :schema="withoutTemplateSchema"
        :with-form-wrapper="false"
        :with-error-message="false"
      />

      <BaseButton
        variant="primary"
        type="submit"
        max-width="335px"
        with-full
        with-decor
        :loading="loading.value"
        :disabled="loading.value"
      >
        Сохранить
      </BaseButton>
    </form>
  </BaseIsland>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { IWithoutTemplateData, IWithoutTemplateFormData } from '~/entities/projects/model/templates-interface'
import type { IFormExpose } from '~/shared/ui'

import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { templateId } from '~/entities/projects/model/const'
import { navigateAfterTemplateSave } from '~/entities/projects/model/navigateAfterTemplateSave'
import { withoutTemplateSchema } from '~/entities/projects/model/templates-schema'
import { uploadProjectImage } from '~/entities/projects/model/uploadProjectImage'
import { useProjectRouteRef } from '~/entities/projects/model/useProjectRouteRef'
import { useTemplateManager } from '~/entities/projects/model/useTemplateManager'
import TemplateMediaSlot from '~/entities/projects/ui/templates/TemplateMediaSlot.vue'
import { useMyProjectRequestLazyQuery, useProjectQuery } from '~/shared/api'
import { isProjectRequestRouteQuery } from '~/shared/const'
import {
  useLoading,
  useNotificationStore,
} from '~/shared/lib'
import { BaseButton, BaseForm, BaseIsland, BaseNavigationBar } from '~/shared/ui'

const router = useRouter()
const route = useRoute()
const { projectId, routeParamForLinks } = useProjectRouteRef(route)

const { uploadFromBlobUrl } = uploadProjectImage()

const isProjectRequestContext = computed(() => isProjectRequestRouteQuery(route.query))

const projectRequestIdFromRoute = computed(() =>
  isProjectRequestContext.value ? (route.params.id as string) : undefined,
)

const { result: projectData } = useProjectQuery(
  () => ({ id: projectId.value }),
  () => ({
    enabled: !isProjectRequestContext.value && Boolean(projectId.value),
    fetchPolicy: 'cache-and-network',
  }),
)

const { result: requestData, load: loadMyProjectRequest } = useMyProjectRequestLazyQuery(
  undefined,
  { fetchPolicy: 'cache-and-network' },
)

watch(
  projectRequestIdFromRoute,
  async (id) => {
    if (!id) {
      return
    }
    await loadMyProjectRequest(null, { id })
  },
  { immediate: true },
)

const { hasExistingTemplate, existingTemplate, saveTemplate } = useTemplateManager(() =>
  isProjectRequestContext.value
    ? { projectRequestId: route.params.id as string }
    : { projectId: projectId.value },
)

const imageForUpload = ref<string | null>(null)
const loading = useLoading()

const formModel = ref<IWithoutTemplateFormData>({
  shortDescription: '',
  detailedDescription: '',
  image: null,
})

watch(
  [projectData, requestData],
  () => {
    const name
      = projectData.value?.project?.name ?? requestData.value?.request?.name
    if (name) {
      formModel.value.shortDescription = name
    }
  },
  { immediate: true },
)

const formRef = ref<ComponentPublicInstance<IFormExpose>>()

watch(existingTemplate, (template) => {
  if (template?.content && hasExistingTemplate.value) {
    const content = template.content as any

    if (content.id === templateId.without_template) {
      formModel.value = {
        shortDescription: content.shortDescription || formModel.value.shortDescription,
        detailedDescription: content.detailedDescription || formModel.value.detailedDescription,
        image: content.image || formModel.value.image,
      }

      imageForUpload.value = content.image || null
    }
  }
}, { immediate: true })

function handleUpdateImage(urls: string[]) {
  imageForUpload.value = urls[0] ?? null
  formModel.value.image = urls[0] ?? null
}

async function handleSave() {
  const result = await formRef.value?.validate()

  if (!result?.valid) {
    return
  }

  let finalImageUrl = formModel.value.image

  if (!finalImageUrl) {
    useNotificationStore().notify({
      type: 'error',
      message: 'Необходимо загрузить все изображения для шаблона',
      hint: 'Пожалуйста, добавьте недостающие изображения',
    })
    return
  }
  await loading.wrapLoading(async () => {
    if (finalImageUrl) {
      const uploadedUrl = await uploadFromBlobUrl(finalImageUrl)
      if (uploadedUrl) {
        finalImageUrl = uploadedUrl
      }
    }

    const templateContent = {
      id: templateId.without_template,
      shortDescription: formModel.value.shortDescription,
      detailedDescription: formModel.value.detailedDescription,
      image: finalImageUrl,
    }

    await saveTemplate(templateContent as IWithoutTemplateData)

    await navigateAfterTemplateSave(
      router,
      route.query,
      isProjectRequestContext.value
        ? (route.params.id as string)
        : routeParamForLinks.value,
    )
  })
}
</script>

<style lang="scss" scoped>
.without-template-edit {
  display: flex;
  flex-direction: column;
  gap: rem(24px);

  @include bp-media('xs', 'sm') {
    gap: rem(16px);
  }

}
</style>
